import { FuncParameterType } from 'tinyest';
import { getAttributesString } from '../../data/attributes.ts';
import { snip } from '../../data/dataTypes.ts';
import {
  type AnyWgslData,
  type AnyWgslStruct,
  isWgslData,
  isWgslStruct,
  Void,
} from '../../data/wgslTypes.ts';
import { MissingLinksError } from '../../errors.ts';
import { getName, setName } from '../../name.ts';
import type { ResolutionCtx } from '../../types.ts';
import {
  addArgTypesToExternals,
  addReturnTypeToExternals,
  applyExternals,
  type ExternalMap,
  replaceExternalsInWgsl,
} from '../resolve/externals.ts';
import { getPrebuiltAstFor } from './astUtils.ts';
import type { Implementation } from './fnTypes.ts';

export interface TgpuFnShellBase<Args extends unknown[], Return> {
  readonly argTypes: Args;
  readonly returnType: Return;
  readonly isEntry: boolean;
}

export interface FnCore {
  applyExternals(newExternals: ExternalMap): void;
  resolve(ctx: ResolutionCtx, fnAttribute?: string): string;
}

export function createFnCore(
  shell: TgpuFnShellBase<unknown[], unknown>,
  implementation: Implementation,
): FnCore {
  /**
   * External application has to be deferred until resolution because
   * some externals can reference the owner function which has not been
   * initialized yet (like when accessing the Output struct of a vertex
   * entry fn).
   */
  const externalsToApply: ExternalMap[] = [];

  if (typeof implementation === 'string') {
    if (!shell.isEntry) {
      addArgTypesToExternals(
        implementation,
        shell.argTypes,
        (externals) => externalsToApply.push(externals),
      );
      addReturnTypeToExternals(
        implementation,
        shell.returnType,
        (externals) => externalsToApply.push(externals),
      );
    } else {
      if (isWgslStruct(shell.argTypes[0])) {
        externalsToApply.push({ In: shell.argTypes[0] });
      }

      if (isWgslStruct(shell.returnType)) {
        externalsToApply.push({ Out: shell.returnType });
      }
    }
  }

  const core = {
    applyExternals(newExternals: ExternalMap): void {
      externalsToApply.push(newExternals);
    },

    resolve(ctx: ResolutionCtx, fnAttribute = ''): string {
      const externalMap: ExternalMap = {};

      for (const externals of externalsToApply) {
        applyExternals(externalMap, externals);
      }

      const id = ctx.names.makeUnique(getName(this));

      if (typeof implementation === 'string') {
        let header = '';

        if (shell.isEntry) {
          const input = isWgslStruct(shell.argTypes[0]) ? '(in: In)' : '()';

          const attributes = isWgslData(shell.returnType)
            ? getAttributesString(shell.returnType)
            : '';
          const output = shell.returnType !== Void
            ? isWgslStruct(shell.returnType)
              ? '-> Out'
              : `-> ${attributes !== '' ? attributes : '@location(0)'} ${
                ctx.resolve(shell.returnType)
              }`
            : '';
          header = `${input} ${output} `;
        }

        const replacedImpl = replaceExternalsInWgsl(
          ctx,
          externalMap,
          `${header}${implementation.trim()}`,
        );

        ctx.addDeclaration(`${fnAttribute}fn ${id}${replacedImpl}`);
      } else {
        // get data generated by the plugin
        const pluginData = getPrebuiltAstFor(implementation);

        if (pluginData?.externals) {
          const missing = Object.fromEntries(
            Object.entries(pluginData.externals).filter(
              ([name]) => !(name in externalMap),
            ),
          );

          applyExternals(externalMap, missing);
        }
        const ast = pluginData?.ast ?? ctx.transpileFn(String(implementation));

        // verify all required externals are present
        const missingExternals = ast.externalNames.filter(
          (name) => !(name in externalMap),
        );
        if (missingExternals.length > 0) {
          throw new MissingLinksError(getName(this), missingExternals);
        }

        // generate wgsl string
        const { head, body } = ctx.fnToWgsl({
          args: shell.argTypes.map((arg, i) =>
            snip(
              ast.params[i]?.type === FuncParameterType.identifier
                ? ast.params[i].name
                : `_arg_${i}`,
              arg as AnyWgslData,
            )
          ),
          argAliases: Object.fromEntries(
            ast.params.flatMap((param, i) =>
              param.type === FuncParameterType.destructuredObject
                ? param.props.map(({ name, alias }) => [
                  alias,
                  snip(
                    `_arg_${i}.${name}`,
                    (shell.argTypes[i] as AnyWgslStruct)
                      .propTypes[name] as AnyWgslData,
                  ),
                ])
                : []
            ),
          ),
          returnType: shell.returnType as AnyWgslData,
          body: ast.body,
          externalMap,
        });

        ctx.addDeclaration(
          `${fnAttribute}fn ${id}${ctx.resolve(head)}${ctx.resolve(body)}`,
        );
      }

      return id;
    },
  };

  // The implementation could have been given a name by a bundler plugin,
  // so we try to transfer it to the core.
  const maybeName = getName(implementation);
  if (maybeName !== undefined) {
    setName(core, maybeName);
  }

  return core;
}
